import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'

// District tenancy: never create or update a real campus. Smoke school ids
// are not on a school-tier JWT, so only district_admin / district_coordinator
// pass administersSchool. Client delete of schools is always denied;
// tagged leftovers are swept by the Admin SDK. schoolStats is function-only.

export default {
  id: 'schoolsTenancy',
  label: 'Schools & school stats',
  description:
    'Direct school create/delete must be denied; licensed creation uses a callable. schoolStats writes stay Cloud-Function-only. Does not touch a real campus.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const smokeId = `smoke_sch_${ctx.runId.slice(-8)}`
    const schoolRef = doc(db, 'schools', smokeId)
    const privateRef = doc(db, 'schools', smokeId, 'private', 'smoke_probe')
    const statsRef = doc(db, 'schoolStats', smokeId)
    const expectCreate = RULE_EXPECTATIONS.schoolCreate(role, {
      schoolId: smokeId,
      tokenSchoolIds: ctx.tokenSchoolIds
    })
    const expectPrivate = RULE_EXPECTATIONS.schoolPrivateWrite(role, {
      schoolId: smokeId,
      tokenSchoolIds: ctx.tokenSchoolIds
    })
    const expectDelete = RULE_EXPECTATIONS.schoolDelete()
    const expectStatsWrite = RULE_EXPECTATIONS.schoolStatsWrite()
    const expectStatsRead = RULE_EXPECTATIONS.schoolStatsRead(role)

    try {
      if (ctx.schoolId) {
        await step('read selected school (expected: allowed)', async () => {
          const { allowed } = await attempt(() => getDoc(doc(db, 'schools', ctx.schoolId)))
          return accessDetail(true, allowed)
        })
      } else {
        step.skip('read selected school', 'No school selected')
      }

      let created = false
      await step(`create smoke school (expected: ${fmtExpected(expectCreate)})`, async () => {
        const { allowed } = await attempt(() =>
          setDoc(schoolRef, {
            name: 'SMOKE TEST — delete me',
            active: false,
            __smokeTest: true,
            __smokeRunId: ctx.runId,
            createdAt: serverTimestamp()
          })
        )
        created = allowed
        return allowed
          ? `${accessDetail(expectCreate, allowed)} — schools/${smokeId}`
          : accessDetail(expectCreate, allowed)
      })

      await step(
        `write school private probe (expected: ${fmtExpected(expectPrivate)})`,
        async () => {
          const { allowed } = await attempt(() =>
            setDoc(privateRef, {
              note: 'SMOKE',
              __smokeTest: true,
              __smokeRunId: ctx.runId
            })
          )
          if (allowed) await deleteDoc(privateRef).catch(() => {})
          return accessDetail(expectPrivate, allowed)
        }
      )

      await step(`delete smoke school (expected: ${fmtExpected(expectDelete)})`, async () => {
        const { allowed } = await attempt(() => deleteDoc(schoolRef))
        if (allowed) created = false
        return accessDetail(expectDelete, allowed)
      })
      if (created) {
        step.skip(
          'client cannot remove smoke school',
          'schools delete is Cloud-Function-only — Admin SDK leftover sweep removes tagged smoke campuses'
        )
      }

      await step(`write school stats (expected: ${fmtExpected(expectStatsWrite)})`, async () => {
        const { allowed } = await attempt(() =>
          setDoc(statsRef, {
            studentCount: 0,
            __smokeTest: true,
            __smokeRunId: ctx.runId
          })
        )
        if (allowed) await deleteDoc(statsRef).catch(() => {})
        return accessDetail(expectStatsWrite, allowed)
      })

      await step(`read school stats (expected: ${fmtExpected(expectStatsRead)})`, async () => {
        const { allowed } = await attempt(() => getDoc(statsRef))
        return accessDetail(expectStatsRead, allowed)
      })
    } finally {
      await deleteDoc(privateRef).catch(() => {})
    }
  }
}
